import { supabase } from './supabase';
import { readAttributionMemory } from './attributionMemory';

// Privacy-safe first-party visit counter. Never sends condition, PHI, or fbclid value.
const SENT_KEY = 'curegate-visit-logged';
const VISITOR_KEY = 'curegate-visitor-key';

// Random anonymous per-browser id used only to count unique visitors. Not PII.
export function getVisitorKey() {
  try {
    let k = localStorage.getItem(VISITOR_KEY);
    if (!k && typeof crypto !== 'undefined' && crypto.randomUUID) {
      k = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, k);
    }
    return k || null;
  } catch { return null; }
}

const TEST_KEY = 'curegate-test-traffic';
// utm_source values reserved for our own QA traffic (also flagged server-side by log_site_visit).
export const QA_UTM_SOURCES = new Set(['qa_test', 'qa', 'internal_test']);

// Test traffic (our own QA runs) is flagged so the Ad Center can exclude it.
export function isTestTraffic() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    // QA runs use ?utm_source=qa_test (or ?cg_test=1); the flag sticks for this browser.
    if (params.get('cg_test') === '1' || QA_UTM_SOURCES.has(String(params.get('utm_source') || '').toLowerCase())) {
      localStorage.setItem(TEST_KEY, '1');
    }
    if (params.get('cg_test') === '0') localStorage.removeItem(TEST_KEY);
    if (localStorage.getItem(TEST_KEY) === '1') return true;
  } catch { /* ignore */ }
  if (QA_UTM_SOURCES.has(String(readAttributionMemory().utm_source || '').toLowerCase())) return true;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /HeadlessChrome|Playwright|puppeteer/i.test(ua) || Boolean(typeof navigator !== 'undefined' && navigator.webdriver);
}

export const FUNNEL_STEPS = ['upload_started', 'extraction_confirmed', 'case_created', 'matches_viewed', 'interested'];

// Landing-funnel actions (first-party only, never sent to Meta). Event name only: no answers,
// no phone, no condition beyond the landing's own cancer key.
export const LANDING_EVENTS = ['quick_screen_started', 'quick_screen_completed', 'contact_submitted', 'callback_requested', 'registration', 'tel_click', 'sms_click', 'talk_click', 'talk_started', 'chat_started', 'talk_30s', 'contact_shared'];
const ONCE_EVENTS = new Set(['landing', 'talk_click', ...FUNNEL_STEPS, 'talk_started', 'chat_started', 'talk_30s', 'quick_screen_started', 'quick_screen_completed', 'registration']);

// Once-events already in flight in this page. sessionStorage is only set after the write
// succeeds, so without this a quick re-render (e.g. the language loading) logged a landing twice.
const inFlight = new Set();

export function logSiteVisit(path, event = 'landing', extra = {}) {
  if (typeof window === 'undefined') return;
  const onceKey = `${SENT_KEY}:${path}:${event}:${extra.caseReference || ''}`;
  const once = ONCE_EVENTS.has(event);
  if (once && inFlight.has(onceKey)) return;
  try { if (once && sessionStorage.getItem(onceKey)) return; } catch { /* ignore */ }
  if (once) inFlight.add(onceKey);
  const m = readAttributionMemory();
  const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent || '');
  supabase.rpc('log_site_visit', {
    p_path: path,
    p_event: event,
    p_utm_source: m.utm_source || null,
    p_utm_medium: m.utm_medium || null,
    p_utm_campaign: m.utm_campaign || null,
    p_utm_content: m.utm_content || null,
    p_has_fbclid: Boolean(m.fbclid),
    p_is_mobile: isMobile,
    p_visitor_key: getVisitorKey(),
    // Landing-page cancer key (e.g. "breast") stays first-party; it is never sent to Meta.
    p_cancer_key: m.cancer_type || null,
    p_case_reference: extra.caseReference || null,
    p_is_test: isTestTraffic(),
  }).then(({ error } = {}) => {
    if (error) { inFlight.delete(onceKey); return; }
    try { sessionStorage.setItem(onceKey, '1'); } catch { /* ignore */ }
  }, () => { inFlight.delete(onceKey); });
}

/** Ad Center funnel step (first-party only). Once per browser session per step (and case). */
export function logFunnelStep(step, { caseReference } = {}) {
  if (!FUNNEL_STEPS.includes(step) || typeof window === 'undefined') return;
  logSiteVisit(window.location.pathname || '/', step, { caseReference });
}

/** Source tag for records created from our own QA runs, so ops reports can exclude them. */
export function qaSourceTag(source) {
  const base = String(source || '').slice(0, 70);
  return isTestTraffic() ? `${base}:qa_test` : base;
}

/** Landing-funnel action (quick screen, call request, tap-to-call, registration). */
export function logLandingEvent(event, path) {
  if (!LANDING_EVENTS.includes(event) || typeof window === 'undefined') return;
  logSiteVisit(path || window.location.pathname || '/', event);
}
